import React, { MouseEvent, useEffect, useRef } from "react";
import classNames from "classnames";
import { useInteraction } from "../../../hooks/useInteraction";

interface Props<T> {
    value: T;
    isChecked: boolean;
    label?: React.ReactNode;
    labelClass?: string;
    handleChange?: (value: T) => void;
    handleChangeEvent?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    type?: "checkbox" | "radio";
    name?: string;
    disabled?: boolean;
    preventClickPropagation?: boolean;
    small?: boolean;
    caption?: string;
    className?: string;
    refCallback?: (ref: HTMLLabelElement) => void;
    isPartiallyChecked?: boolean;
}

export const FormCheckbox = <T extends string | number>({
    value,
    isChecked,
    label,
    labelClass,
    handleChange,
    handleChangeEvent,
    type = "checkbox",
    name,
    disabled,
    preventClickPropagation,
    small,
    caption,
    className,
    refCallback,
    isPartiallyChecked,
}: Props<T>) => {
    const interaction = useInteraction(
        (e: MouseEvent) => preventClickPropagation && e.stopPropagation(),
    );
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = !!isPartiallyChecked;
        }
    }, [isPartiallyChecked]);

    return (
        <label
            data-testid={`form-checkbox-${value}-label`}
            className={classNames("form-check", className, {
                "form-check--small": small,
                "form-check--partially-checked": isPartiallyChecked,
            })}
            ref={refCallback}
            {...interaction}
        >
            <input
                ref={inputRef}
                data-testid={`form-checkbox-${value}-input`}
                className="form-check-input"
                name={name}
                type={type}
                value={value}
                checked={isChecked}
                onChange={(e) => {
                    if (handleChangeEvent) {
                        handleChangeEvent(e);
                    }
                    if (handleChange) {
                        handleChange(value);
                    }
                }}
                disabled={disabled}
            />
            <span className={classNames("form-check-label", labelClass)}>
                <span>
                    {label}
                    {caption && (
                        <span className="form-check__caption">{caption}</span>
                    )}
                </span>
            </span>
        </label>
    );
};
